import { MSym } from '@storeo/ui';
import { useAuth } from '../lib/auth';
import { DemoBanner, PlaceholderTag } from '../components/lumina/Placeholder';

const RBAC = [
  { cap: 'Ver dados fiscais e sensíveis de lojistas', roles: [true, false, true] },
  { cap: 'Executar saques e transferências manuais', roles: [true, false, true] },
  { cap: 'Alterar limites de risco e antifraude', roles: [true, true, false] },
  { cap: 'Ver chaves de API e secrets de produção', roles: [true, true, false] },
];
const ROLE_LABELS = ['Super Admin', 'Tech / DevOps', 'Financeiro'];

/** Admin · Usuários & Acessos — nova (Fluxo 4 Fase 2). A Storeo tem 1 role (`platform-staff`)
 *  no Keycloak, sem papéis granulares (RBAC) nem 2FA/hardware-key obrigatório — a linha do
 *  usuário logado é real (via `claims`), o resto (equipe, matriz RBAC) é ilustrativo. */
export function Usuarios() {
  const { claims } = useAuth();
  const person = claims?.name ?? claims?.email ?? 'Equipe';

  return (
    <>
      <div className="relative overflow-hidden rounded-2xl bg-surface-container-low p-space-xl shadow-xl">
        <div className="relative z-10 flex flex-col gap-space-2xs">
          <h1 className="font-headline-lg text-headline-lg text-on-surface font-bold flex items-center gap-2">
            Usuários & Acessos <PlaceholderTag />
          </h1>
          <p className="font-body-md text-body-md text-on-surface-variant">
            A Storeo tem 1 role (<code>platform-staff</code>) no Keycloak — sem papéis granulares (RBAC) nem 2FA obrigatório hoje.
          </p>
        </div>
      </div>

      <DemoBanner>Só a sua linha ({person}) é real. RBAC granular, hardware key e a "equipe" abaixo são ilustrativos.</DemoBanner>

      <section className="bg-surface-container-low rounded-2xl p-space-md shadow-lg overflow-hidden">
        <div className="flex items-center gap-space-xs px-space-sm pb-space-sm">
          <MSym name="badge" className="text-primary text-[20px]" />
          <h2 className="font-headline-sm text-headline-sm text-on-surface font-bold">Equipe</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="text-outline font-label-sm text-label-sm uppercase tracking-wider bg-surface-container-lowest/60">
                <th className="py-space-sm px-space-md rounded-l-lg">Membro</th>
                <th className="py-space-sm px-space-sm">Papel</th>
                <th className="py-space-sm px-space-sm">2FA</th>
                <th className="py-space-sm px-space-md rounded-r-lg">Status</th>
              </tr>
            </thead>
            <tbody className="text-body-sm font-body-sm">
              <tr className="border-t border-surface-container-highest/20">
                <td className="py-space-md px-space-md">
                  <span className="text-on-surface font-semibold">{person}</span>
                  <span className="ml-space-xs font-label-sm text-label-sm text-secondary">(você)</span>
                </td>
                <td className="py-space-md px-space-sm text-on-surface-variant">platform-staff</td>
                <td className="py-space-md px-space-sm text-outline">não obrigatório</td>
                <td className="py-space-md px-space-md text-secondary font-semibold">Ativo</td>
              </tr>
              {['Colega exemplo A', 'Colega exemplo B'].map((name) => (
                <tr key={name} className="border-t border-surface-container-highest/20 opacity-60">
                  <td className="py-space-md px-space-md text-on-surface">{name}</td>
                  <td className="py-space-md px-space-sm text-on-surface-variant">exemplo</td>
                  <td className="py-space-md px-space-sm text-outline">—</td>
                  <td className="py-space-md px-space-md text-outline">—</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="flex flex-col gap-space-md">
        <h2 className="font-headline-sm text-headline-sm text-on-surface font-bold flex items-center gap-1">
          Matriz de Permissões (RBAC) <PlaceholderTag />
        </h2>
        <div className="bg-surface-container-low rounded-2xl p-space-md shadow-lg overflow-hidden opacity-80">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="text-outline font-label-sm text-label-sm uppercase tracking-wider bg-surface-container-lowest/60">
                  <th className="py-space-sm px-space-md rounded-l-lg">Capacidade</th>
                  {ROLE_LABELS.map((r) => (
                    <th key={r} className="py-space-sm px-space-sm text-center">{r}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="text-body-sm font-body-sm">
                {RBAC.map((row) => (
                  <tr key={row.cap} className="border-t border-surface-container-highest/20">
                    <td className="py-space-md px-space-md text-on-surface-variant">{row.cap}</td>
                    {row.roles.map((ok, i) => (
                      <td key={i} className="py-space-md px-space-sm text-center">
                        <MSym name={ok ? 'check_circle' : 'cancel'} className={`text-[18px] ${ok ? 'text-secondary' : 'text-outline/40'}`} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </>
  );
}
